import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";

import { AnalyzeAPI, Ai360UserAPI } from "@ai360/core";
import { RadioButtonGroup, RadioButton, Button, Loader, LoaderTypes } from "~/core";
import { PrintIcon } from "~/core/icons";
import { DialogTool } from "~/core/components/dialog-box/dialog-tool";
import { DialogBox, DialogBoxFooterType } from "~/core";
import { actions as notificationActions, MSGTYPE } from "~/notifications";
import { selectors as cdSelectors } from "~/customer-data";
import { selectors as loginSelectors } from "~/login";
import { useAppSelector } from "~/store/hooks";

import MarkdownRenderer from "./components/markdownrenderer";
import NutrientChart from "./components/nutrientchart";
import SoilTissueChart from "./components/soilTissueChart";
import PlanterYieldChart from "./components/planterYieldChart";
import { messages, creditMessages, errorMessages } from "./i18n-messages";
import { logFirebaseEvent } from "~/utils/firebase";

import { getAllowedPrompts } from "./selectors";
import { actions as analyzeActions } from "./actions";

import "./analyze.css";
import "react-quill/dist/quill.snow.css";
import "../core/components/text-editor/text-editor.css";
import "../core/components/radio-button/radio-button.css";

export function Analyze() {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const allowedPrompts = useAppSelector(getAllowedPrompts);
    const loginGuid = useAppSelector(loginSelectors.getLoginGuid);
    const userGuid = useAppSelector(loginSelectors.getTheUserGuid);

    const {
        data: creditData,
        isLoading: isCreditLoading,
        refetch: refetchCredit,
    } = Ai360UserAPI.useCheckBedrockCreditQuery(
        { loginGuid, userGuid },
        { skip: !loginGuid || !userGuid, refetchOnMountOrArgChange: true }
    );

    /* IMPORTANT to include a new analyze question add entry here */

    const analyzeData = {
        "6a0eab37-0ed0-4ffb-8314-c32b31842254": {
            // Analyze type guid
            displayMsg: formatMessage(messages.question1),
            firebaseMsg: "analyze_soil_deficiency",
            id: 1, // Question id for bedrock api
        },
        "aef33fcf-9344-4ce5-ac6d-028d414a570b": {
            displayMsg: formatMessage(messages.question2),
            firebaseMsg: "analyze_soil_and_tissue_deficiency",
            id: 2,
        },
        "d57a8f83-4cd4-4cdc-a26b-4c3e3824a17d": {
            displayMsg: formatMessage(messages.question3),
            firebaseMsg: "analyze_tissue_not_soil_deficiency",
            id: 3,
        },
        "016ca1f7-b45c-45b8-b8e2-e8424de37f89": {
            displayMsg: formatMessage(messages.question4),
            firebaseMsg: "analyze_planter_performance_by_yield",
            id: 4,
        },
    };

    let allowedGuids;
    if (allowedPrompts === null) {
        allowedGuids = new Set();
    } else {
        allowedGuids = new Set(allowedPrompts.map((p) => p.analyzeTypeGuid));
    }

    const allowedAnalyzeQuestions = Object.fromEntries(
        Object.entries(analyzeData).filter(([guid]) => allowedGuids.has(guid))
    );

    const [selected, setSelected] = useState<string>(null);

    const [output, setOutput] = useState("");
    const [textOutput, setTextOutput] = useState("");
    const [pdfOutput, setPdfOutput] = useState(undefined);

    const [graphOutputSoil, setGraphOutputSoil] = useState(undefined);
    const [graphOutputTissueSoil, setGraphOutputTissueSoil] = useState(undefined);
    const [graphOutputPlanterYield, setGraphOutputPlanterYield] = useState(undefined);
    const [legendOutput, setLegendOutput] = useState(undefined);

    const [isStreaming, setIsStreaming] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [invalidFields, setInvalidFields] = useState([]);
    const [fieldsModal, setFieldsModal] = useState<[string | undefined, string | undefined]>([
        undefined,
        undefined,
    ]);

    const selectedFieldGuidSet = useAppSelector(cdSelectors.getSelectedFieldGuids);

    useEffect(() => {
        if (allowedPrompts === null) {
            dispatch(analyzeActions.fetchAllowedPrompts());
        }
    }, []);

    useEffect(() => {
        let fieldsText = "";
        if (invalidFields.length !== 0) {
            invalidFields.forEach((field) => {
                fieldsText += `${field}\n`;
            });
            fieldsText = "\n" + fieldsText.slice(0, -1);
            setFieldsModal([
                formatMessage(errorMessages.invalidFieldsWarning, { fieldsText }),
                formatMessage(errorMessages.invalidFieldsModalTitle),
            ]);
        }
    }, [invalidFields]);

    useEffect(() => {
        const lines = output
            .split(/\n|\}\{/)
            .filter((line) => line.trim() !== "")
            .map((line, i, arr) => {
                const trimmed = line.trim();
                if (
                    i < arr.length - 1 &&
                    !trimmed.endsWith("}") &&
                    i > 0 &&
                    !trimmed.startsWith("{")
                )
                    return "{" + trimmed + "}";
                if (i < arr.length - 1 && !trimmed.endsWith("}")) return trimmed + "}";
                if (i > 0 && !trimmed.startsWith("{")) return "{" + trimmed;
                return trimmed;
            });

        let textAdd = "";
        let soilGraphOut = undefined;
        let tissueSoilGraphOut = undefined;
        let planterYieldGraphOut = undefined;
        let pdfOut = undefined;

        for (const line of lines) {
            try {
                const parsed = JSON.parse(line);

                switch (parsed.type) {
                    case "content_block_delta":
                        if (parsed.delta?.type === "text_delta") {
                            textAdd += parsed.delta.text;
                        }
                        break;
                    case "GraphModel_Soil":
                        soilGraphOut = parsed.GraphModel_Soil;
                        break;
                    case "GraphModel_TissueSoil":
                        tissueSoilGraphOut = parsed.GraphModel_TissueSoil;
                        break;
                    case "GraphModel_PlanterYield":
                        planterYieldGraphOut = parsed.GraphModel_PlanterYield;
                        break;
                    case "levelNames":
                        setLegendOutput(
                            parsed.levelNames.split("\n").map((line) => line.split("\t"))
                        );
                        break;
                    case "pdf":
                        pdfOut = parsed.pdf;
                        break;
                }
            } catch {
                // Partial JSON line, skip and wait for next chunk
            }
        }
        setTextOutput(textAdd);
        setGraphOutputSoil(soilGraphOut);
        setGraphOutputTissueSoil(tissueSoilGraphOut);
        setGraphOutputPlanterYield(planterYieldGraphOut);
        setPdfOutput(pdfOut);
        if (
            textAdd != "" ||
            soilGraphOut != undefined ||
            tissueSoilGraphOut != undefined ||
            planterYieldGraphOut != undefined
        ) {
            setIsLoading(false);
        }
    }, [output]);

    const handleSubmit = async () => {
        if (isStreaming) {
            return;
        }
        if (creditData && !creditData.isAllowed) {
            setFieldsModal([
                formatMessage(creditMessages.creditLimitReachedMessage),
                formatMessage(creditMessages.creditLimitReachedTitle),
            ]);
            return;
        }
        if (selected === null || selectedFieldGuidSet.size === 0) {
            setFieldsModal([
                formatMessage(errorMessages.invalidSelectionError),
                formatMessage(errorMessages.invalidSelectionModalTitle),
            ]);
            return;
        }

        logFirebaseEvent(allowedAnalyzeQuestions[selected].firebaseMsg);

        setOutput("");
        setIsStreaming(true);
        setIsLoading(true);

        try {
            await AnalyzeAPI.getAnalyzeInfo(
                Array.from(selectedFieldGuidSet),
                allowedAnalyzeQuestions[selected].id,
                loginGuid,
                (chunk) => setOutput((prev) => prev + chunk),
                () => setIsStreaming(false),
                (fields) => setInvalidFields(fields)
            );
        } catch (error) {
            const apiResult = error["apiResultObj"];
            const message = apiResult?.["innerMessage"] ?? apiResult?.["message"];
            const title = apiResult?.["innerMessage"] != null ? apiResult?.["message"] : null;
            if (message) {
                setFieldsModal([message, title ?? formatMessage(errorMessages.genericModalTitle)]);
            } else {
                dispatch(
                    notificationActions.pushToasterMessage(
                        formatMessage(errorMessages.generic),
                        MSGTYPE.ERROR
                    )
                );
            }
        }
        setIsStreaming(false);
        setIsLoading(false);
        refetchCredit();
    };

    const onPrint = () => {
        if (pdfOutput === undefined) {
            return "";
        }

        const date = new Date()
            .toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
            })
            .replace(/\//g, "-");

        const randomNum = String(Math.floor(Math.random() * 1000000)).padStart(6, "0");

        const fileName = `AnalysisResult_${date}_${randomNum}`;

        const linkSource = `data:application/pdf;base64,${pdfOutput}`;
        const downloadLink = document.createElement("a");

        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
    };

    const modal = (
        <DialogBox
            isOpen={fieldsModal[0] !== undefined}
            onClose={() => {
                setFieldsModal([undefined, undefined]);
            }}
            footerType={DialogBoxFooterType.NONE}
            title={fieldsModal[1]}
        >
            {fieldsModal[0]?.split("\n").map((line, i, arr) => (
                <span key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                </span>
            ))}
        </DialogBox>
    );

    const radioButtons = (
        <div className="analysisOptions">
            <RadioButtonGroup
                afterOnChange={(value) => {
                    if (typeof value !== "boolean") {
                        setSelected(value);
                    }
                }}
                selectedValue={selected}
            >
                {Object.entries(allowedAnalyzeQuestions).map(([value, label]) => (
                    <RadioButton key={value} label={label.displayMsg} value={value} />
                ))}
            </RadioButtonGroup>
        </div>
    );

    const getCreditProgressColor = () => {
        if (!creditData) return "green";
        const progress =
            creditData.dailyLimit > 0 ? creditData.currentUsage / creditData.dailyLimit : 0;
        if (!creditData.isAllowed) return "red";
        if (progress >= 0.8) return "orange";
        if (progress >= 0.5) return "yellow";
        return "green";
    };

    const creditFooter = creditData ? (
        <div className="credit-footer">
            <div className="credit-progress-bar">
                <div
                    className={`credit-progress-fill ${getCreditProgressColor()}`}
                    style={{
                        width: `${Math.min(
                            (creditData.currentUsage / creditData.dailyLimit) * 100,
                            100
                        )}%`,
                    }}
                />
            </div>
            <span
                className={`credit-label ${
                    getCreditProgressColor() === "red"
                        ? "red"
                        : getCreditProgressColor() === "orange"
                        ? "orange"
                        : ""
                }`}
            >
                {formatMessage(creditMessages.creditsUsedToday, {
                    current: creditData.currentUsage,
                    limit: creditData.dailyLimit,
                })}
            </span>
        </div>
    ) : isCreditLoading ? (
        <div className="credit-footer">
            <Loader className="layer-loader" type={LoaderTypes.LINE_SCALE_PULSE_OUT_RAPID} />
        </div>
    ) : null;

    const selectAnalysisTypeSection = (
        <section>
            <div className="topRowConainter">
                <h1 className="sectionTitle">{formatMessage(messages.select)}</h1>
                <Button type="analyze" forceSubmit onClick={handleSubmit} />
            </div>
            {radioButtons}
            {creditFooter}
        </section>
    );

    const resultSection = (
        <div className="resultSection">
            <div className="topRowConainter">
                <h1 className="sectionTitle">{formatMessage(messages.result)}</h1>
                <DialogTool onClick={() => onPrint()}>
                    <PrintIcon className="printIcon" />
                </DialogTool>
            </div>
            <div className="ql-container ql-snow" id="analysis-result">
                <div className="ql-editor">
                    <div className="analysis-result">
                        <MarkdownRenderer markdown={textOutput} />
                        {graphOutputSoil && graphOutputSoil.length > 0 && (
                            <NutrientChart data={graphOutputSoil} legendData={legendOutput} />
                        )}
                        {graphOutputTissueSoil && graphOutputTissueSoil.length > 0 && (
                            <SoilTissueChart
                                data={graphOutputTissueSoil}
                                legendData={legendOutput}
                            />
                        )}
                        {graphOutputPlanterYield && graphOutputPlanterYield.length > 0 && (
                            <PlanterYieldChart data={graphOutputPlanterYield} />
                        )}
                        <p style={{ fontStyle: "italic" }}>{formatMessage(messages.aiWarning)}</p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="add-edit-panel" style={{ overflow: "hidden", margin: "15px 20px" }}>
            <div className="analyze">
                {selectAnalysisTypeSection}
                {isLoading ? (
                    <Loader
                        className="layer-loader"
                        type={LoaderTypes.LINE_SCALE_PULSE_OUT_RAPID}
                    />
                ) : null}
                {output === "" ? <></> : resultSection}
            </div>
            {modal}
        </div>
    );
}
